/**
 * PATH: packages/ui/src/kanban/components/card-detail/EmailLinkTickCrossSlot.tsx
 * FILE_ID: UI-KANBAN-CMP-118
 * PURPOSE: Lazy tick/cross confirm slot for the email link row.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { isTrelloMirrorCard } from '../email-from-card-parse'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'

export interface EmailLinkTickCrossSlotProps {
  provider?: string | null
  externalId: string | null
  confirmFn?: (
    externalId: string,
    emailId?: string | null,
  ) => Promise<unknown>
  rejectFn?: (
    externalId: string,
    emailId?: string | null,
  ) => Promise<unknown>
  fetchDetail?: (externalId: string) => Promise<TrelloCardDetail>
}

export function EmailLinkTickCrossSlot({
  provider,
  externalId,
  confirmFn,
  rejectFn,
  fetchDetail,
}: EmailLinkTickCrossSlotProps) {
  const enabled = Boolean(
    fetchDetail && isTrelloMirrorCard({ provider, external_id: externalId }),
  )
  const q = useQuery<TrelloCardDetail>({
    queryKey: ['trello-card-detail', externalId],
    queryFn: () => fetchDetail!(externalId as string),
    enabled,
    staleTime: 30_000,
  })

  if (!enabled) return null
  const emailLink = q.data?.email_link
  if (!emailLink) return null
  if (!emailLink.can_confirm && !emailLink.can_reject) {
    // No actionable link AND no prior decisions to show — render
    // nothing so the email view starts at the top of the pane.
    if (!emailLink.decisions || emailLink.decisions.length === 0) {
      return null
    }
  }

  return (
    <EmailLinkTickCross
      externalId={externalId as string}
      emailLink={emailLink}
      onConfirm={confirmFn}
      onReject={rejectFn}
    />
  )
}
